import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TrayMenu from '@ux/tray-menu';
import '@ux/tray-menu/styles';
import { cookie, URL } from '@ux/util';
import golfToEid from '@gd-internal/golf-to-eid';
import { IntlProvider, FormattedMessage } from '@godaddy/react-mintl';
import { Atlas, envFromHostname } from '@godaddy/atlas';

/**
 * Load currencies from Atlas API for a given market and private label.
 *
 * @param {string} atlasEnv Atlas environment (prod, dev, test, stg, ote)
 * @param {number} privateLabelId Private label ID
 * @param {string} marketLocale Market locale code (e.g. 'en-US', 'ru-RU')
 * @returns {Promise<Array>} Promise resolving to [priorityCurrencies, standardCurrencies]
 */
const loadCurrenciesFromAtlas = async (atlasEnv, privateLabelId, marketLocale) => {
  // Determine environment from hostname if needed
  if (!atlasEnv) {
    const hostname = typeof window !== 'undefined' && window.location.hostname;
    if (hostname) {
      atlasEnv = envFromHostname(hostname);
    }
  }
  atlasEnv ??= 'prod';

  // Build Atlas instance for the environment
  const atlas = await Atlas.builder(atlasEnv).build();

  // Resolve market of brand using private label ID
  const brand = atlas.resolveBrandByPlid(privateLabelId);
  const market = brand.resolveMarketByLocale(marketLocale);

  // Extract currencies specific to this market
  const priority = market.getCurrencies();

  // Create set of market currency codes for filtering
  const codes = new Set(priority.map(c => c.code));

  // Filter out market currencies from all available currencies
  const standard = atlas.allCurrencies.filter(c => !codes.has(c.code));

  // Return priority and standard currencies
  return [priority, standard];
};

/**
 * Render and expose the CurrencySelector as a functional Component. The CurrencySelector
 * is responsible for allowing users to select their desired money. The default values
 * populated and the ordering of currencies within the list are dependent on the current
 * market and any previous user state.
 *
 * @param {Object} props Properties
 * @returns {JSX.Element|null} Currency selector component or null if not loaded
 * @api public
 */
const CurrencySelector = ({
  currency,
  eidPrefix = 'uxp.hyd',
  messages,
  urls,
  market,
  privateLabelId,
  isReseller,
  env
}) => {
  const [priorityCurrencies, setPriorityCurrencies] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selected, setSelected] = useState('');
  const [loaded, setLoaded] = useState(false);
  const trayRef = useRef(null);

  /**
   * Do currency redirect once set
   *
   * @param {Object} cur The currently selected currency object
   */
  const doCurrencyRedirect = useCallback((cur) => {
    if (isReseller) {
      const url = new URL(urls.currencyList.href, true);

      url.set('query', {
        ...url.query,
        currency: cur.code,
        target: window.location.href
      });

      window.location.assign(url.href);

      return;
    }

    const requestUrl = new URL(urls.currencyList.href + '/' + cur.code);
    const query = {};
    if (privateLabelId !== 1) {
      query.pl_id = privateLabelId;
    }

    requestUrl.set('query', query);
    fetch(requestUrl.href)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to set currency preference');
        }
        const redirectUrl = new URL(window.location.href, true);
        if (redirectUrl.query.currency) {
          // strip `currencyType` query
          redirectUrl.set('query', {
            ...redirectUrl.query,
            currency: ''
          });
          // If the URL is changing, we must truly redirect to it
          window.location.assign(redirectUrl.href);
        } else {
          // If the URL is the same, we can simply reload, so that hash URLs work
          window.location.reload();
        }
      })
      .catch(() => {
        // Silently fail on error, same as original behavior
      });
  }, [isReseller, urls, privateLabelId]);

  /**
   * Function that gets called when user clicks on a currency item
   *
   * @param {Object} cur The currently selected currency object
   * @param {React.SyntheticEvent} event The event fired on click
   *
   * @api public
   */
  const onCurrencyChanged = useCallback((cur, event) => {
    event.preventDefault();
    setSelected(cur.code);

    if (trayRef.current) {
      trayRef.current.close();
    }

    if (typeof document === 'undefined') {
      return;
    }

    doCurrencyRedirect(cur);
  }, [doCurrencyRedirect]);

  /**
   * Populate state with a list of currencies (from the Markets API).
   * @param {Array} marketCurrencies List of market's currencies (e.g. { code: CAD, symbol: '$' })
   * @param {Array} otherCurrencies List of remaining currencies (e.g. { code: CAD, symbol: '$' })
   * @param {string} selectedCode The currency code of the currency selected by the currency cookie
   * @api private
   */
  const setCurrenciesState = useCallback((marketCurrencies, otherCurrencies, selectedCode) => {
    setCurrencies(otherCurrencies);
    setPriorityCurrencies(marketCurrencies);
    setSelected(selectedCode || 'USD');
    setLoaded(true);
  }, []);

  /**
   * Perform an AJAX call to the GUI service and pass the set
   * the currencies represented by the results on this instance.
   * @api public
   */
  const fetchCurrencies = useCallback(async () => {
    try {
      // ux.eldorado.data.currency passed in by CMS
      let selectedCurrency = currency;
      // string value of the `currency` cookie
      const currencyCookie = cookie.get('currency');

      const currencyGroups = await loadCurrenciesFromAtlas(env, privateLabelId, market);

      // validate selected
      currencyGroups.flat().forEach(curr => {
        if (curr.code === currencyCookie) {
          selectedCurrency = curr.code;
        }
      });

      const [priority, standard] = currencyGroups;
      setCurrenciesState(priority, standard, selectedCurrency);
    } catch (err) {
      // Silently fail - component will remain in non-loaded state
      // TODO: Consider adding error state and user feedback
    }
  }, [currency, env, privateLabelId, market, setCurrenciesState]);

  /**
   * Fetches the list of currencies from the GUI service if no
   * currencies are present.
   */
  useEffect(() => {
    if (typeof document !== 'undefined') {
      fetchCurrencies();
    }
  }, [fetchCurrencies]);

  // Do not render anything if the currencies are not yet fetched
  if (!loaded) return null;

  /**
   * Helper function to render a single currency item
   * @param {Object} cur Currency object with code and symbol
   * @param {string} keyPrefix Prefix for React key (priority or currency)
   * @returns {JSX.Element} CurrencyItem component
   */
  const renderCurrency = (cur, keyPrefix) => {
    const isSelected = selected === cur.code;
    const itemClassName = classnames(
      'currency-item',
      {
        selected: isSelected
      }
    );

    return (
      <CurrencyItem
        key={ `${keyPrefix}-${cur.code}` }
        code={ cur.code }
        symbol={ cur.symbol }
        className={ itemClassName }
        eidPrefix={ eidPrefix }
        isSelected={ isSelected }
        onClick={ onCurrencyChanged }
      />
    );
  };

  const trayMenuClassNames = classnames(
    'currency-selector',
    'currency-selector-fade',
    {
      'currency-selector-in': loaded
    }
  );
  const eid = golfToEid('CurrencySelector', {
    prefix: eidPrefix,
    postfix: 'click'
  });

  return (
    <IntlProvider messages={ messages }>
      <FormattedMessage id='Sales:Footer:CurrencySelectorTray:AriaLabel' values={{ selection: selected }}>
        {content => (
          <TrayMenu
            ariaLabel={ Array.isArray(content) ? content.join('') : content }
            name={ selected }
            type='base'
            className={ trayMenuClassNames }
            ref={ trayRef }
            data-eid={ eid }>
            {priorityCurrencies.map((cur) => renderCurrency(cur, 'priority'))}
            <div className='divider'></div>
            {currencies.map((cur) => renderCurrency(cur, 'currency'))}
          </TrayMenu>
        )}
      </FormattedMessage>
    </IntlProvider>
  );
};

/*
 * PropTypes for the CurrencySelector.
 * urls.currencyList.href is required for currency switching.
 */
CurrencySelector.propTypes = {
  // i18n messages object with string keys/values
  messages: PropTypes.objectOf(PropTypes.string).isRequired,
  currency: PropTypes.string,
  eidPrefix: PropTypes.string,
  urls: PropTypes.shape({
    currencyList: PropTypes.shape({
      href: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  market: PropTypes.string.isRequired,
  privateLabelId: PropTypes.number.isRequired,
  isReseller: PropTypes.bool,
  // Atlas environment (prod, dev, test, stg, ote)
  env: PropTypes.oneOf(['prod', 'dev', 'test', 'stg', 'ote'])
};

export default CurrencySelector;

/**
 * Internal helper component for displaying a single currency in the
 * CurrencySelector dropdown.
 *
 * @param {Object} props Properties
 * @returns {React.ReactNode} react component
 * @api private
 */
const CurrencyItem = ({
  code,
  symbol,
  eidPrefix,
  onClick,
  className,
  isSelected
}) => {
  const handleClick = (event) => {
    onClick({ code, symbol }, event);
  };

  const eid = golfToEid(code, {
    prefix: eidPrefix + '.currency_selector',
    postfix: 'click'
  });
  const ariaLabelCurrency = code + ', ' + symbol;

  return (
    <button
      type='button'
      className={ className }
      onClick={ handleClick }
      data-eid={ eid }
      aria-label={ ariaLabelCurrency }
      { ...(isSelected && { 'aria-current': 'true' }) }>
      <span>{code}</span> <span>{symbol}</span>
    </button>
  );
};

/*
 * PropTypes for the CurrencyItem.
 * Only `className` is optional.
 */
CurrencyItem.propTypes = {
  className: PropTypes.string,
  eidPrefix: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  symbol: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};
